import { isLikelyUserId } from '@/utils/userDisplay';

export type AssetLabelSource = {
  id: string;
  asset_code?: string | null;
  asset_name?: string | null;
};

export type JournalVoucherLabelSource = {
  id: string;
  voucher_number?: string | null;
};

/** Display label for asset pickers and tables: `AST-… — Name`. */
export function formatAssetOption(a: AssetLabelSource): string {
  const code = String(a.asset_code ?? '').trim();
  const name = String(a.asset_name ?? '').trim();
  if (code && name) return `${code} — ${name}`;
  if (code) return code;
  if (name) return name;
  return 'Untitled asset';
}

export function buildAssetIdToLabelMap(assets: AssetLabelSource[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const a of assets) {
    m.set(a.id, formatAssetOption(a));
  }
  return m;
}

export function resolveAssetDisplayFromMap(
  map: Map<string, string>,
  id: string | null | undefined,
  emptyLabel = '—'
): string {
  if (id == null || id === '') return emptyLabel;
  const trimmed = String(id).trim();
  const hit = map.get(trimmed);
  if (hit) return hit;
  if (isLikelyUserId(trimmed)) return 'Unknown asset';
  return trimmed;
}

/** Prefer voucher number; never show a truncated UUID as the primary label. */
export function formatJournalVoucherLabel(
  voucherId: string | null | undefined,
  voucherNumber?: string | null,
  emptyLabel = '—'
): string {
  if (voucherNumber && String(voucherNumber).trim()) return String(voucherNumber).trim();
  if (voucherId == null || voucherId === '') return emptyLabel;
  if (isLikelyUserId(String(voucherId))) return 'Journal voucher';
  return String(voucherId);
}

export function buildJournalVoucherIdToLabelMap(
  rows: Array<{ journal_voucher_id?: string | null; journal_voucher_number?: string | null }>
): Map<string, string> {
  const m = new Map<string, string>();
  for (const r of rows) {
    const id = r.journal_voucher_id;
    if (!id) continue;
    m.set(id, formatJournalVoucherLabel(id, r.journal_voucher_number));
  }
  return m;
}

export function resolveJournalVoucherDisplayFromMap(
  map: Map<string, string>,
  id: string | null | undefined,
  emptyLabel = '—'
): string {
  if (id == null || id === '') return emptyLabel;
  const trimmed = String(id).trim();
  const hit = map.get(trimmed);
  if (hit) return hit;
  if (isLikelyUserId(trimmed)) return 'Journal voucher';
  return trimmed;
}
